import CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import EventScope from 'owa-service/lib/contract/EventScope';
import friendlyEndDateForAllDay from 'owa-calendar-forms-common/lib/utils/friendlyEndDateForAllDay';
import { duplicateEvent } from '../actions/duplicateEvent';
import { fetchFullCalendarEventAndUpdateItemResponseShapeType } from 'owa-calendar-forms-common/lib/helpers/eventStore';
import { initializeCalendarEvents } from 'owa-calendar-compose-form-actions';
import { isException, isOccurrence } from 'owa-calendar-event-capabilities';
import { orchestrator } from 'satcheljs';
import { lazyShowFullCompose } from 'owa-calendar-compose-form-store';
import { toJS } from 'mobx';
import initializeCalendarFolderId from 'owa-calendar-forms-common/lib/utils/initializeCalendarFolderId';
import AttendeeType from 'owa-service/lib/contract/AttendeeType';
import { ClientFolderId } from 'owa-client-ids';
import { getCalendarIdByFolderId, getAllCalendars, isWritableCalendar } from 'owa-calendar-cache';
import { lazyCreateFluidCollabSpace, parseCollabSpace } from 'owa-calendar-collaborative-space';
import { FluidOwaSource } from 'owa-fluid-validations';
import { isFeatureEnabled } from 'owa-feature-flags';
import { removeDownlevelLink } from 'owa-calendar-compose-form-store/lib/utils/collabSpaceComposeUtils';

/**
 * Duplicates the event that is passed and starts compose form with it
 */
export default orchestrator(duplicateEvent, async actionMessage => {
    const event: CalendarEvent = actionMessage.event;

    // Fetch full calendar event using fetchFullCalendarEventAndUpdateItemResponseShapeType with 'WithBlockedExternalContent'
    // to unblock external images since we are in a compose scenario.
    const scope = actionMessage.useDefaultScope ? EventScope.Default : inferScope(event);
    const { fullEventPromise } = fetchFullCalendarEventAndUpdateItemResponseShapeType(
        event.ItemId,
        'WithBlockedExternalContent',
        null,
        null /* getExistingCalendarEvent */,
        scope ===
            EventScope.AllInstancesInSeries /** isfetchFullSeriesMasterCalendarEventFromEventInstance */,
        { fetchSource: 'DuplicateEvent' }
    );

    // We are making a copy of the event since we don't want to be mutating the store instance
    let eventCopy: CalendarEvent = toJS(await fullEventPromise);
    eventCopy.ItemId = null;
    if (eventCopy.IsOnlineMeeting) {
        // if It's an online meeting, then null out the rest of the online meeting parameters,
        // otherwise, it will have the online meeting properties of the original meeting
        eventCopy.OnlineMeetingConferenceId = null;
        eventCopy.OnlineMeetingJoinUrl = null;
        eventCopy.OnlineMeetingTollFreeNumbers = null;
        eventCopy.OnlineMeetingTollNumber = null;
        eventCopy.JoinOnlineMeetingUrl = null;
        eventCopy.SkypeTeamsProperties = null;
    }

    if (eventCopy.HasAttachments) {
        // If we have attachments, then remove them since we don't support attachments while duplicating
        // WI: 39463
        eventCopy.Attachments = null;
    }

    if (eventCopy.IsAllDayEvent) {
        // If the item is allDay we have to remove one day form the end date so it displays correctly to the user
        // A one day event is saved with start: May 18 and end: May 19, but
        // the start and end displayed to the user are start: May 18 and end: May 18
        eventCopy.End = friendlyEndDateForAllDay(eventCopy.End);
    }

    if (!isWritableCalendarFromParentFolderId(eventCopy.ParentFolderId)) {
        // if the calendar of the event is not writable, then pick the first writable calendar that is selected on the surface
        eventCopy.ParentFolderId = initializeCalendarFolderId();
    }

    if (eventCopy.RequiredAttendees) {
        // If we have required attendees, then clear the attendee responses
        eventCopy.RequiredAttendees = clearAttendeeResponses(eventCopy.RequiredAttendees);
    }

    if (!eventCopy.IsOrganizer && eventCopy.Organizer && eventCopy.Organizer.Mailbox.EmailAddress) {
        // Make sure organizer is added to required attendees, if not add it.
        // We don't need to add the organizer if IsOrganizer is true or the current user is the organizer
        const organizerEmail = eventCopy.Organizer.Mailbox.EmailAddress;
        const organizers = eventCopy.RequiredAttendees.filter(
            attendee => attendee?.Mailbox && attendee.Mailbox.EmailAddress == organizerEmail
        );
        if (organizers.length === 0) {
            // If organizer is not part of the RequiredAttendees collection, add them to it
            eventCopy.RequiredAttendees.push(<AttendeeType>{ Mailbox: organizerEmail });
        }
    }

    if (eventCopy.OptionalAttendees) {
        // If we have optional attendees, then clear the attendee responses
        eventCopy.OptionalAttendees = clearAttendeeResponses(eventCopy.OptionalAttendees);
    }

    if (eventCopy.Resources) {
        // If we have resources, then clear the resource responses
        eventCopy.Resources = clearAttendeeResponses(eventCopy.Resources);
    }

    if (eventCopy.InboxReminders) {
        // If we have inbox reminders (email reminders), then clear the Id of the email reminder since it is set by the server
        eventCopy.InboxReminders.forEach(reminder => (reminder.Id = undefined));
    }

    // unset MeetingRequestWasSent since it is set by the server
    eventCopy.MeetingRequestWasSent = undefined;

    // Remove ConversationId so that the new event gets its own conversation
    eventCopy.ConversationId = undefined;

    let createNewCollabSpace = false;
    if (isFeatureEnabled('cal-cmp-fluidCollaborativeSpace') && eventCopy.CollabSpace) {
        const parsedCollabSpace = parseCollabSpace(eventCopy.CollabSpace);
        if (parsedCollabSpace.collabAllowed) {
            createNewCollabSpace = true;
            eventCopy.Body.Value = removeDownlevelLink(eventCopy.Body.Value);
        }
        // Remove existing collab space as the new event would have its own new collab space
        eventCopy.CollabSpace = null;
    }

    // Import first to ensure mutators are registered before calling
    // initializeCalendarEvents, this will be updated with future refactoring
    // of the compose initialization path
    // https://outlookweb.visualstudio.com/Outlook%20Web/_workitems/edit/112918
    const showFullCompose = await lazyShowFullCompose.import();

    initializeCalendarEvents(eventCopy);

    await showFullCompose(
        false /* isCharmExplicitlySet */,
        actionMessage.entrySource,
        actionMessage.viewType
    );

    if (createNewCollabSpace) {
        lazyCreateFluidCollabSpace.importAndExecute(
            eventCopy,
            FluidOwaSource.CalendarCompose,
            null
        );
    }
});

/**
 * If scope is not passed, then we infer the scope based on the below logic:
 * If it is a regular event, then scope is Default
 * If it is a recurrence exception, then scope is ThisInstanceOnly
 * If it is a recurrence occurrence, then scope is AllInstancesInSeries
 * @param event The event
 */
function inferScope(event: CalendarEvent) {
    if (isException(event.CalendarItemType)) {
        // If its an exception of a recurrence, then we duplicate the exception (without the recurrence pattern)
        return EventScope.ThisInstanceOnly;
    } else if (isOccurrence(event.CalendarItemType)) {
        // If it is an occurrence of a recurrence, then we duplicate the recurrence master
        return EventScope.AllInstancesInSeries;
    }

    return EventScope.Default;
}

/**
 * Clear the attendees's response type
 */
function clearAttendeeResponses(attendees: AttendeeType[]): AttendeeType[] {
    return attendees.map(attendee => <AttendeeType>{ Mailbox: attendee.Mailbox });
}

/**
 * Checks if the calendar corresponding to the parent folder id is writable
 */
function isWritableCalendarFromParentFolderId(parentFolderId: ClientFolderId): boolean {
    const selectedCalendarId = getCalendarIdByFolderId(parentFolderId.Id);
    if (!selectedCalendarId) {
        return false;
    }
    const calendar = getAllCalendars().filter(c => c.id.id == selectedCalendarId.id)[0];

    return isWritableCalendar(calendar);
}
